// src/composables/useMinors.ts
import { ref } from 'vue';

export type MinorRow = {
  name: string;    
  code: string;        
  college: string;     
  collegeId?: string;  
  collegeShort?: string; 
};

export function useMinors() {
  const isLoading = ref(false);
  const errorMsg = ref('');

  function parseCSV(text: string): { rows: string[][]; delim: string } {
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    const firstLine = (normalized.split('\n').find(l => l.trim().length) ?? '');
    const delim = [',', ';', '\t'].reduce((best, d) => {
      const score = (firstLine.match(new RegExp(`\\${d}`, 'g')) || []).length;
      return score > best.score ? { d, score } : best;
    }, { d: ',', score: -1 as number }).d;

    const rows: string[][] = [];
    let cur: string[] = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < normalized.length; i++) {
      const ch = normalized[i];
      const next = normalized[i + 1];

      if (inQuotes) {
        if (ch === '"' && next === '"') { field += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { field += ch; }
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === delim) { cur.push(field); field = ''; }
        else if (ch === '\n') { cur.push(field); rows.push(cur); cur = []; field = ''; }
        else { field += ch; }
      }
    }
    if (field.length || cur.length) { cur.push(field); rows.push(cur); }

    return { rows: rows.filter(r => r.some(c => (c ?? '').trim().length)), delim };
  }

  async function getActiveMinorsFromCsv(): Promise<MinorRow[]> {
    try {
      isLoading.value = true;
      errorMsg.value = '';

      const res = await fetch('/data/ActiveMinors2025.csv', { cache: 'no-store' });
      if (!res.ok) throw new Error(`CSV fetch failed: ${res.status} ${res.statusText}`);

      const text = await res.text();
      const { rows } = parseCSV(text);
      if (!rows.length) return [];

      // --- header detection (case-insensitive) ---
      const hdr = rows[0].map(v => (v ?? '').trim().toLowerCase());

      const hasHeader = hdr.some(h =>
        h.includes('academic plan') ||
        h.includes('description') ||
        h === 'college' ||
        h.includes('college id') ||
        h.includes('academic program')
      );

      // indices (with fallbacks)
      let start = 0;

      let codeIdx = -1;        // Academic Plan
      let nameIdx = -1;        // Academic Plan Description
      let collegeIdx = -1;     // Academic Program (BEST for S3 COLLEGE)
      let collegeIdIdx = -1;   // College ID
      let collegeShortIdx = -1;// College

      if (hasHeader) {
        codeIdx = hdr.findIndex(h => h === 'academic plan' || h.includes('academic plan'));
        nameIdx = hdr.findIndex(h => h.includes('academic plan description') || (h.includes('description') && !h.includes('degree')));
        collegeIdx = hdr.findIndex(h => h.includes('academic program'));
        collegeIdIdx = hdr.findIndex(h => h.includes('college id'));
        collegeShortIdx = hdr.findIndex(h => h === 'college');

        // fallbacks if something is missing
        if (codeIdx < 0) codeIdx = 0;
        if (nameIdx < 0) nameIdx = 1;
        if (collegeIdx < 0) {
          collegeIdx = (collegeShortIdx >= 0 ? collegeShortIdx : 4);
        }

        start = 1;
      } else {
        codeIdx = 0;
        nameIdx = 1;
        collegeIdx = 4;
        start = 0;
      }

      const minors: MinorRow[] = [];

      for (let i = start; i < rows.length; i++) {
        const r = rows[i];

        const code = (r[codeIdx] ?? '').trim();         // Academic Plan
        const name = (r[nameIdx] ?? '').trim();         // Academic Plan Description
        const college = (r[collegeIdx] ?? '').trim();   // Academic Program -> S3 COLLEGE

        const collegeId = (collegeIdIdx >= 0 ? (r[collegeIdIdx] ?? '').trim() : '');
        const collegeShort = (collegeShortIdx >= 0 ? (r[collegeShortIdx] ?? '').trim() : '');

        if (!code || !name || !college) continue;

        minors.push({
          name,
          code,
          college,
          collegeId: collegeId || undefined,
          collegeShort: collegeShort || undefined,
        });
      }

      const seen = new Set<string>();
      const unique = minors.filter(m => {
        const k = `${m.code}|${m.college}`.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });

      console.debug('[Minors CSV] Parsed rows:', unique.length);
      return unique;
    } catch (e: any) {
      console.error('[Minors CSV] Error:', e);
      errorMsg.value = e?.message ?? String(e);
      return [];
    } finally {
      isLoading.value = false;
    }
  }

  return { getActiveMinorsFromCsv, isLoading, errorMsg };
}
